import React, { useState, useCallback } from 'react'
import { PrometheusTheme } from '@prometheus/types'
import { useSelector } from '../hooks/useSelector'
import { Box, Button, Card, Divider, Input, Modal, Stack, Typography } from '@prometheus/components'
import { useDispatch } from 'react-redux'
import { setDisplayName, setTheme } from '@/reducers/auth/auth'
import { applyTheme } from '../utils/theme'
import usePlayerActions from '@/views/SheetView/hooks/usePlayerActions'
import { saveGuestSession } from '../utils/guestSession'

interface EditProfileModalProps {
  open: boolean
  onCancel: () => void
}

/**
 * The profile save goes through the server (`updateProfile`) rather than
 * straight to firestore: a guest has no firebase login, so the browser can't
 * write their user document — and one path for both kinds of player is one
 * fewer thing to keep in step.
 */
export const EditProfileModal = ({ open, onCancel }: EditProfileModalProps) => {
  const uid = useSelector(s => s.auth.uid)
  const theme = useSelector(s => s.auth.theme)
  const guestSession = useSelector(s => s.auth.guestSession)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const dispatch = useDispatch()
  const { onUpdateProfile } = usePlayerActions()

  const onSubmit = useCallback(async (name: string) => {
    setSaving(true)
    setError(null)

    try {
      const { data } = await onUpdateProfile({ name })
      dispatch(setDisplayName(data?.name ?? name))
      // Keep the stored guest session in step so the name survives a reload.
      if (guestSession) {
        saveGuestSession({ ...guestSession, name: data?.name ?? name })
      }
      onCancel()
    } catch (e) {
      setError((e as any)?.message || 'Could not save your profile.')
    } finally {
      setSaving(false)
    }
  }, [onUpdateProfile, guestSession, onCancel, dispatch])

  // Apply + persist theme on click so the change is visible instantly.
  const onThemeChange = useCallback(async (next: PrometheusTheme) => {
    applyTheme(next)
    dispatch(setTheme(next))
    if (uid) {
      await onUpdateProfile({ theme: next }).catch(e => console.error(e))
    }
  }, [uid, onUpdateProfile, dispatch])

  if (!open) return null
  return (
    <Modal onClose={onCancel}>
      <Card padding="none">
        <Box space="sm">
          <Typography variant="h4" retro color="primary">User Profile</Typography>
        </Box>
        <Box space="base">
          <Input label="Display Name (Default anonymous)" value={name} onChange={e => setName(e.target.value)} />
        </Box>
        <Box space="base">
          <Stack space="sm">
            <Typography variant="caption" color="text-muted">Theme</Typography>
            <div style={{ display: 'flex', gap: 'var(--prometheus-spacing-sm)' }}>
              <Button variant="light" outline={theme !== 'light'} onClick={() => onThemeChange('light')}>Light</Button>
              <Button variant="dark" outline={theme !== 'dark'} onClick={() => onThemeChange('dark')}>Dark</Button>
            </div>
          </Stack>
        </Box>
        {!!error && (
          <Box space="base">
            <Typography variant="caption" color="danger">{error}</Typography>
          </Box>
        )}
        <Divider />
        <Box space="sm">
          <div style={{ display: 'flex', gap: 'var(--prometheus-spacing-sm)', justifyContent: 'flex-end' }}>
            <Button variant="warning" outline onClick={onCancel}>Cancel</Button>
            <Button variant="primary" loading={saving} disabled={saving} onClick={() => onSubmit(name)}>Save</Button>
          </div>
        </Box>
      </Card>
    </Modal>
  )
}
